import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { cajaApi } from '../services/api';

const ConfiguracionCaja = () => {
  const { user, isAdmin } = useAuth();
  const { sucursales, sucursalActual } = useSucursal();
  const [activeTab, setActiveTab] = useState(isAdmin() ? 'cuentas' : 'categorias');
  
  const [cuentas, setCuentas] = useState([]);
  const [pos, setPos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);

  const tiendaIdParam = isAdmin() ? (sucursalActual?.id || 'todas') : user?.tienda_id;

  useEffect(() => {
    cargarDatos();
  }, [tiendaIdParam]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const tienda_id = tiendaIdParam !== 'todas' ? tiendaIdParam : undefined;
      const [resCuentas, resPos, resCategorias] = await Promise.all([
        cajaApi.getCuentas(tienda_id),
        cajaApi.getPos(tienda_id),
        cajaApi.getCategorias(tienda_id)
      ]);
      setCuentas(resCuentas || []);
      setPos(resPos || []);
      setCategorias(resCategorias || []);
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const accionesPorTipo = {
    cuentas: { crear: cajaApi.crearCuenta, actualizar: cajaApi.actualizarCuenta, eliminar: cajaApi.eliminarCuenta },
    pos: { crear: cajaApi.crearPos, actualizar: cajaApi.actualizarPos, eliminar: cajaApi.eliminarPos },
    categorias: { crear: cajaApi.crearCategoria, actualizar: cajaApi.actualizarCategoria, eliminar: cajaApi.eliminarCategoria },
  };

  const handleCreate = async (tipo, data) => {
    try {
      await accionesPorTipo[tipo].crear(data);
      cargarDatos();
    } catch (error) {
      alert('Error al crear: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdate = async (tipo, data) => {
    try {
      await accionesPorTipo[tipo].actualizar(data);
      cargarDatos();
    } catch (error) {
      alert('Error al actualizar: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (tipo, id) => {
    if (!window.confirm('¿Está seguro de eliminar este elemento?')) return;
    try {
      await accionesPorTipo[tipo].eliminar(id);
      cargarDatos();
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.error || error.message));
    }
  };

  // Generic Form Component
  const Form = ({ tipo, item = {}, onSave, onCancel }) => {
    const [formData, setFormData] = useState(item);

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <form onSubmit={handleSubmit} className="card">
        <div className="form-group">
          <label className="form-label">Nombre</label>
          <input required type="text" className="form-control" value={formData.nombre || ''} onChange={e => setFormData({...formData, nombre: e.target.value})} />
        </div>

        {tipo === 'cuentas' && (
          <>
            <div className="form-group">
              <label className="form-label">Banco</label>
              <input type="text" className="form-control" value={formData.banco || ''} onChange={e => setFormData({...formData, banco: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Número de Cuenta</label>
              <input type="text" className="form-control" value={formData.numero_cuenta || ''} onChange={e => setFormData({...formData, numero_cuenta: e.target.value})} />
            </div>
          </>
        )}

        {tipo === 'categorias' && (
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-control" value={formData.tipo || 'Variable'} onChange={e => setFormData({...formData, tipo: e.target.value})}>
              <option value="Variable">Variable</option>
              <option value="Fijo">Fijo</option>
            </select>
          </div>
        )}

        {isAdmin() && (
          <div className="form-group">
            <label className="form-label">Sucursal</label>
            <select className="form-control" value={formData.tienda_id || ''} onChange={e => setFormData({...formData, tienda_id: e.target.value || null})}>
              <option value="">Global (Todas)</option>
              {sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="submit" className="btn btn-primary">Guardar</button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
        </div>
      </form>
    );
  };

  const CrudSection = ({ titulo, tipo, data }) => {
    const [editingItem, setEditingItem] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className="card-title" style={{ marginBottom: 0 }}>{titulo}</h3>
          <button className="btn btn-primary" onClick={() => { setIsCreating(true); setEditingItem(null); }}>
            Nuevo
          </button>
        </div>

        {isCreating && (
          <Form
            tipo={tipo}
            item={{tienda_id: tiendaIdParam !== 'todas' ? tiendaIdParam : null}}
            onSave={(item) => { handleCreate(tipo, item); setIsCreating(false); }}
            onCancel={() => setIsCreating(false)}
          />
        )}

        {editingItem && (
          <Form
            tipo={tipo}
            item={editingItem}
            onSave={(item) => { handleUpdate(tipo, item); setEditingItem(null); }}
            onCancel={() => setEditingItem(null)}
          />
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                {tipo === 'cuentas' && <th>Banco</th>}
                {tipo === 'categorias' && <th>Tipo</th>}
                <th>Sucursal</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.map(item => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 500 }}>{item.nombre}</td>
                  {tipo === 'cuentas' && <td>{item.banco}</td>}
                  {tipo === 'categorias' && <td>{item.tipo}</td>}
                  <td>{item.tienda_id ? sucursales.find(s => s.id === item.tienda_id)?.nombre : 'Global'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditingItem(item); setIsCreating(false); }}>Editar</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(tipo, item.id)}>Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-light)' }}>No hay registros</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Configuración de Caja</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isAdmin() && (
            <>
              <button className={`btn ${activeTab === 'cuentas' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('cuentas')}>Cuentas Bancarias</button>
              <button className={`btn ${activeTab === 'pos' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('pos')}>Terminales POS</button>
            </>
          )}
          <button className={`btn ${activeTab === 'categorias' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('categorias')}>Categorías de Egreso</button>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="card">
          {activeTab === 'cuentas' && isAdmin() && <CrudSection titulo="Cuentas Bancarias" tipo="cuentas" data={cuentas} />}
          {activeTab === 'pos' && isAdmin() && <CrudSection titulo="Terminales POS" tipo="pos" data={pos} />}
          {activeTab === 'categorias' && <CrudSection titulo="Categorías de Egreso" tipo="categorias" data={categorias} />}
        </div>
      )}
    </div>
  );
};

export default ConfiguracionCaja;

